import React, { useState, useMemo } from 'react';
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import ExportacaoRelatorios from "@/components/financeiro/ExportacaoRelatorios";

export function FaturasAdmin({ selectedPropId, refresh, onRefresh }) {
    const [filterStatus, setFilterStatus] = useState('todos');
    const [filterText, setFilterText] = useState('');
    const [cycleField, setCycleField] = useState('criacao');
    const [cycleYm, setCycleYm] = useState('');
    const [emitOpen, setEmitOpen] = useState(false);
    const [emitPropId, setEmitPropId] = useState('');
    const [emitDescricao, setEmitDescricao] = useState('');
    const [emitValor, setEmitValor] = useState('');
    const [emitVencimento, setEmitVencimento] = useState('');
    const [emitGerarPix, setEmitGerarPix] = useState(true);
    const [emitLoading, setEmitLoading] = useState(false);
    const [emitError, setEmitError] = useState('');
    const [fatDetOpen, setFatDetOpen] = useState(false);
    const [fatSel, setFatSel] = useState(null);

    const { data: faturas = [], isLoading } = useQuery({
        queryKey: ['faturas', refresh],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('faturas')
                .select('*')
                .order('created_date', { ascending: false })
                .limit(300);
            if (error) return [];
            return data || [];
        }
    });

    const { data: fatLogs = [] } = useQuery({
        queryKey: ['fatura_logs', fatSel?.id || 'none'],
        queryFn: async () => {
            if (!fatSel?.id) return [];
            const { data, error } = await agrogestao.client
                .from('audit_logs')
                .select('created_at, acao, status, details')
                .eq('table', 'faturas')
                .order('created_at', { ascending: false })
                .limit(50);
            if (error) return [];
            return data || [];
        },
        enabled: !!fatSel?.id
    });

    const { data: licDet } = useQuery({
        queryKey: ['licenca_by_id', fatSel?.licenca_id || 'none'],
        queryFn: async () => {
            if (!fatSel?.licenca_id) return null;
            const { data, error } = await agrogestao.client
                .from('licencas')
                .select('*')
                .eq('id', fatSel.licenca_id)
                .maybeSingle();
            if (error) return null;
            return data || null;
        },
        enabled: !!fatSel?.licenca_id
    });

    const reativarLicenca = async () => {
        try {
            if (!fatSel?.licenca_id) throw new Error('Fatura sem licença vinculada');
            const l = licDet;
            if (!l) throw new Error('Licença não encontrada');
            let base = new Date();
            let until = new Date(base.getTime());
            const plano = String(l.plano || '');
            if (plano === 'anual') { until.setFullYear(until.getFullYear() + 1); }
            else { until.setMonth(until.getMonth() + 1); }
            const payload = { status: 'ativo', valid_until: until.toISOString() };
            const { error } = await agrogestao.client.from('licencas').update(payload).eq('id', l.id);
            if (error) throw error;
            onRefresh && onRefresh();
        } catch (e) {
            alert(e?.message || 'Falha ao reativar licença');
        }
    };

    const { data: propriedades = [] } = useQuery({
        queryKey: ['props_for_faturas'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('propriedades')
                .select('id, nome, created_by')
                .order('nome', { ascending: true })
                .limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    // Calculate propsById for easier lookup in details view
    const propsById = useMemo(() => {
        const map = {};
        propriedades.forEach(p => { map[String(p.id)] = p; });
        return map;
    }, [propriedades]);

    const filtered = useMemo(() => {
        return (faturas || []).filter(f => {
            if (selectedPropId && f.propriedade_id && String(f.propriedade_id) !== String(selectedPropId)) return false;
            if (filterStatus !== 'todos') {
                const s = String(f.status || '');
                if (filterStatus === 'emitida' && s !== 'pendente') return false;
                else if (filterStatus === 'paga' && s !== 'pago') return false;
                else if (filterStatus === 'vencida' && s !== 'expirado') return false;
                else if (filterStatus === 'cancelada' && s !== 'cancelado') return false;
                else if (filterStatus === 'falha' && s !== 'falha') return false;
            }
            if (filterText && !String(f.descricao || '').toLowerCase().includes(filterText.toLowerCase())) return false;
            if (cycleYm) {
                const d = cycleField === 'vencimento' ? (f.vencimento ? new Date(f.vencimento) : null) : (f.created_date ? new Date(f.created_date) : null);
                const ym = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '';
                if (ym !== cycleYm) return false;
            }
            return true;
        });
    }, [faturas, selectedPropId, filterStatus, filterText, cycleYm, cycleField]);

    const enviarLembrete = async (f) => {
        try {
            const subj = `Lembrete de pagamento - Fatura #${f.id}`;
            const body = `Olá!\n\nTemos uma fatura pendente no valor de ${(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.\nDescrição: ${f.descricao || ''}.\n\nCódigo PIX (copia e cola): ${f.pix_brcode || ''}.\n\nObrigado!`;
            if (f.created_by) {
                await agrogestao.integrations.Core.SendEmail({ to: f.created_by, subject: subj, content: body });
            }
            const next = new Date();
            next.setDate(next.getDate() + 3);
            await agrogestao.client
                .from('faturas')
                .update({ dunning_nivel: Number(f.dunning_nivel || 0) + 1, dunning_ultimo_contato: new Date().toISOString(), dunning_proximo_contato: next.toISOString() })
                .eq('id', f.id);
            onRefresh && onRefresh();
        } catch (e) {
            alert('Falha ao enviar lembrete');
        }
    };

    const emitirManual = async () => {
        try {
            setEmitError('');
            setEmitLoading(true);
            const pid = emitPropId || selectedPropId;
            if (!pid) throw new Error('Selecione a propriedade');
            const valorCents = Math.round(Number(emitValor || 0) * 100);
            if (!valorCents || valorCents <= 0) throw new Error('Informe valor válido');
            const payload = {
                propriedade_id: pid,
                descricao: emitDescricao || `Fatura manual (${pid})`,
                valor_centavos: valorCents,
                status: 'pendente',
                vencimento: emitVencimento ? new Date(emitVencimento).toISOString() : null,
                metodo_pagamento: emitGerarPix ? 'pix' : 'manual'
            };
            const { data, error } = await agrogestao.client.from('faturas').insert(payload).select('*').limit(1);
            if (error) throw error;
            const created = Array.isArray(data) ? data[0] : data;
            if (emitGerarPix) {
                try {
                    await agrogestao.functions.invoke('criarQRCodePixLicenca', { propriedade_id: pid, plano: 'mensal', description: emitDescricao || 'Fatura manual' });
                } catch { }
            }
            setEmitOpen(false);
            setEmitPropId('');
            setEmitDescricao('');
            setEmitValor('');
            setEmitVencimento('');
            setEmitGerarPix(true);
            onRefresh && onRefresh();
            setFatSel(created);
            setFatDetOpen(true);
        } catch (e) {
            setEmitError(e?.message || 'Falha ao emitir fatura');
        } finally {
            setEmitLoading(false);
        }
    };

    const marcarPago = async (f) => {
        try {
            await agrogestao.client
                .from('faturas')
                .update({ status: 'pago', paga_em: new Date().toISOString() })
                .eq('id', f.id);
            onRefresh && onRefresh();
        } catch (e) {
            alert('Falha ao marcar como pago');
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Input placeholder="Buscar descrição" value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todas</SelectItem>
                        <SelectItem value="emitida">Emitidas</SelectItem>
                        <SelectItem value="paga">Pagas</SelectItem>
                        <SelectItem value="vencida">Vencidas</SelectItem>
                        <SelectItem value="cancelada">Canceladas</SelectItem>
                        <SelectItem value="falha">Falhas</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={cycleField} onValueChange={setCycleField}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="criacao">Ciclo por Criação</SelectItem>
                        <SelectItem value="vencimento">Ciclo por Vencimento</SelectItem>
                    </SelectContent>
                </Select>
                {(() => {
                    const set = new Set();
                    (faturas || []).forEach(f => {
                        const d = cycleField === 'vencimento' ? (f.vencimento ? new Date(f.vencimento) : null) : (f.created_date ? new Date(f.created_date) : null);
                        if (!d) return;
                        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        set.add(ym);
                    });
                    const options = Array.from(set).sort((a, b) => b.localeCompare(a));
                    return (
                        <Select value={cycleYm || 'todos'} onValueChange={(v) => setCycleYm(v === 'todos' ? '' : v)}>
                            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos os ciclos</SelectItem>
                                {options.map(o => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    );
                })()}
                <Dialog open={emitOpen} onOpenChange={setEmitOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-emerald-600 hover:bg-emerald-700">Emitir fatura manual</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl md:max-w-3xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Emitir fatura manual</DialogTitle>
                            <DialogDescription>Preencha os dados da fatura</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label>Propriedade</Label>
                                <Select value={emitPropId || selectedPropId || ''} onValueChange={setEmitPropId}>
                                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(propriedades || []).map(p => (
                                            <SelectItem key={p.id} value={String(p.id)}>{p.nome || p.id}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Descrição</Label>
                                <Input value={emitDescricao} onChange={(e) => setEmitDescricao(e.target.value)} />
                            </div>
                            <div>
                                <Label>Valor (R$)</Label>
                                <Input type="number" step="0.01" value={emitValor} onChange={(e) => setEmitValor(e.target.value)} />
                            </div>
                            <div>
                                <Label>Vencimento</Label>
                                <Input type="date" value={emitVencimento} onChange={(e) => setEmitVencimento(e.target.value)} />
                            </div>
                            <div className="flex items-center gap-2">
                                <input id="emitGerarPix" type="checkbox" checked={emitGerarPix} onChange={(e) => setEmitGerarPix(e.target.checked)} />
                                <Label htmlFor="emitGerarPix">Gerar cobrança PIX</Label>
                            </div>
                        </div>
                        {emitError && <div className="text-red-600 text-sm">{emitError}</div>}
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setEmitOpen(false)}>Cancelar</Button>
                            <Button onClick={emitirManual} disabled={emitLoading} className="bg-emerald-600 hover:bg-emerald-700">{emitLoading ? 'Emitindo…' : 'Emitir'}</Button>
                        </div>
                    </DialogContent>
                </Dialog>
                <ExportacaoRelatorios
                    dados={filtered.map(f => ({ ...f, valor: Number(f.valor_centavos || 0) / 100 }))}
                    nomeArquivo="faturas"
                    colunas={[
                        { label: 'Criada', accessor: (f) => f.created_date, type: 'date' },
                        { label: 'Propriedade', accessor: (f) => { const p = propriedades.find(p => String(p.id) === String(f.propriedade_id)); return p?.nome || String(f.propriedade_id || ''); } },
                        { label: 'Descrição', accessor: (f) => f.descricao },
                        { label: 'Valor', accessor: (f) => f.valor, type: 'currency' },
                        { label: 'Status', accessor: (f) => f.status },
                        { label: 'Dunning', accessor: (f) => Number(f.dunning_nivel || 0) }
                    ]}
                />
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b">
                            <th className="text-left py-3 px-4">Criada</th>
                            <th className="text-left py-3 px-4">Propriedade</th>
                            <th className="text-left py-3 px-4">Licença</th>
                            <th className="text-left py-3 px-4">Descrição</th>
                            <th className="text-left py-3 px-4">Vencimento</th>
                            <th className="text-right py-3 px-4">Valor</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">PIX</th>
                            <th className="text-left py-3 px-4">Dunning</th>
                            <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={10} className="py-6 text-center">Carregando...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={10} className="py-6 text-center text-stone-500">Nenhuma fatura encontrada</td></tr>
                        ) : filtered.map((f) => (
                            <tr key={f.id} className="border-b">
                                <td className="py-3 px-4">{f.created_date ? format(new Date(f.created_date), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                <td className="py-3 px-4">{(() => { const p = propriedades.find(p => String(p.id) === String(f.propriedade_id)); return p?.nome || String(f.propriedade_id || ''); })()}</td>
                                <td className="py-3 px-4 text-xs">{String(f.licenca_id || '-')}</td>
                                <td className="py-3 px-4">{f.descricao || '-'}</td>
                                <td className="py-3 px-4">{f.vencimento ? format(new Date(f.vencimento), 'dd/MM/yyyy') : '-'}</td>
                                <td className="py-3 px-4 text-right">{(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                <td className="py-3 px-4">
                                    <Badge className={f.status === 'pago' ? 'bg-green-100 text-green-700' : f.status === 'pendente' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-700'}>{f.status}</Badge>
                                </td>
                                <td className="py-3 px-4 text-xs">{f.metodo_pagamento === 'pix' ? (f.pix_brcode ? 'PIX (com QR)' : 'PIX') : '-'}</td>
                                <td className="py-3 px-4 text-xs">Nível {Number(f.dunning_nivel || 0)}{f.dunning_proximo_contato ? ` • Próx.: ${format(new Date(f.dunning_proximo_contato), 'dd/MM/yyyy')}` : ''}</td>
                                <td className="py-3 px-4">
                                    <div className="flex flex-wrap gap-2">
                                        {f.status !== 'pago' && <Button onClick={() => enviarLembrete(f)} className="bg-indigo-600 hover:bg-indigo-700">Reenviar fatura</Button>}
                                        {f.status !== 'pago' && <Button onClick={() => marcarPago(f)} className="bg-green-600 hover:bg-green-700">Marcar pago</Button>}
                                        <Button variant="outline" onClick={() => { setFatSel(f); setFatDetOpen(true); }}>Detalhes</Button>
                                        <Button variant="outline" onClick={() => {
                                            const w = window.open('', '_blank');
                                            if (!w) return;
                                            const html = `<!doctype html><html><head><meta charset="utf-8"><title>Fatura ${f.id}</title><style>body{font-family:ui-sans-serif,system-ui; padding:24px} .row{margin-bottom:8px} .title{font-weight:600} .badge{display:inline-block;padding:4px 8px;border-radius:8px;background:#eee} .qr{width:200px;height:200px;object-contain;margin-top:16px}</style></head><body>` +
                                                `<div class='row'><span class='title'>Fatura</span> #${f.id}</div>` +
                                                `<div class='row'>Criada: ${f.created_date ? format(new Date(f.created_date), 'dd/MM/yyyy HH:mm') : '-'}</div>` +
                                                `<div class='row'>Propriedade: ${(() => { const p = propriedades.find(p => String(p.id) === String(f.propriedade_id)); return p?.nome || String(f.propriedade_id || ''); })()}</div>` +
                                                `<div class='row'>Licença: ${String(f.licenca_id || '-')}</div>` +
                                                `<div class='row'>Descrição: ${f.descricao || '-'}</div>` +
                                                `<div class='row'>Vencimento: ${f.vencimento ? format(new Date(f.vencimento), 'dd/MM/yyyy') : '-'}</div>` +
                                                `<div class='row'>Valor: ${(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>` +
                                                `<div class='row'>Status: <span class='badge'>${f.status}</span></div>` +
                                                `${f.metodo_pagamento === 'pix' ? `<div class='row'>PIX: ${f.pix_brcode || '-'}</div>` : ''}` +
                                                `${f.pix_qrcode_base64 ? `<img src='${f.pix_qrcode_base64}' class='qr'/>` : ''}` +
                                                `</body></html>`;
                                            w.document.write(html);
                                            try { w.document.close(); w.focus(); setTimeout(() => { w.print(); }, 300); } catch { }
                                        }}>PDF</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Dialog open={fatDetOpen} onOpenChange={setFatDetOpen}>
                <DialogContent className="max-w-2xl md:max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Detalhes da Fatura</DialogTitle>
                        <DialogDescription>Relação PIX e histórico</DialogDescription>
                    </DialogHeader>
                    {fatSel && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Card className="bg-indigo-50 border-indigo-200">
                                    <CardHeader><CardTitle className="text-sm">Resumo</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm">ID: {fatSel.id}</div>
                                        <div className="text-sm">Propriedade: {(() => { const p = propsById[String(fatSel.propriedade_id || '')]; return p?.nome || String(fatSel.propriedade_id || ''); })()}</div>
                                        <div className="text-sm">Licença: {String(fatSel.licenca_id || '-')}</div>
                                        <div className="text-sm">Valor: {(Number(fatSel.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                        <div className="text-sm">Vencimento: {fatSel.vencimento ? format(new Date(fatSel.vencimento), 'dd/MM/yyyy') : '-'}</div>
                                        <div className="text-sm">Status: {fatSel.status}</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-amber-50 border-amber-200">
                                    <CardHeader><CardTitle className="text-sm">PIX</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm">Método: {fatSel.metodo_pagamento || '-'}</div>
                                        <div className="text-sm break-all">BR Code: {fatSel.pix_brcode || '-'}</div>
                                        {fatSel.pix_qrcode_base64 && <img src={fatSel.pix_qrcode_base64} alt="QR" className="mt-2 w-48 h-48 object-contain" />}
                                        {fatSel.status === 'pago' && licDet && String(licDet.status || '') !== 'ativo' && (
                                            <div className="mt-3">
                                                <Button onClick={reativarLicenca} className="bg-green-600 hover:bg-green-700">Reativar licença</Button>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                            <Card>
                                <CardHeader><CardTitle className="text-sm">Histórico da Fatura</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Data</th>
                                                    <th className="text-left py-2 px-2">Ação</th>
                                                    <th className="text-left py-2 px-2">Status</th>
                                                    <th className="text-left py-2 px-2">Detalhes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(fatLogs || []).length === 0 ? (
                                                    <tr><td colSpan={4} className="py-4 text-center text-stone-500">Sem histórico</td></tr>
                                                ) : (fatLogs || []).map((r, idx) => (
                                                    <tr key={idx} className="border-b">
                                                        <td className="py-2 px-2">{r.created_at ? format(new Date(r.created_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                                        <td className="py-2 px-2">{r.acao || '-'}</td>
                                                        <td className="py-2 px-2">{r.status || '-'}</td>
                                                        <td className="py-2 px-2 text-xs break-all">{typeof r.details === 'string' ? r.details : JSON.stringify(r.details || {})}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
